import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useCallback, useMemo } from 'react';
import { formatDecimal } from '../../bidAnalysisTemplate.logic';
import classes from '../../bidAnalysisTemplate.module.scss';

export interface ContractPriceRankingItem {
  ndc: string;
  contracter_name: string | null; // API can return null
  labeller_name: string;
  price_type: string;
  price: number;
  price_eu: number;
  current_wac: number | null; // API can return null
  current_market_share: string | null; // API returns string like "0.78%" or can be null
  extra_data: {
    contract_start_date: string | null;
    percentage_of_commercial_market: string | null;
    percentage_of_government_market: string | null;
    nad: string | null;
    nad_expiry: string | null;
  };
  price_rank: number;
  uniqueKey?: string; // Added for DataTable row expansion
}

interface ContractPriceRankingTableProps {
  data: ContractPriceRankingItem[];
  expandedRows: any;
  onRowToggle: (e: any) => void;
  isLoading?: boolean;
  onPriceAgainstClick?: (competitor: ContractPriceRankingItem) => void;
}

export const ContractPriceRankingTable: React.FC<ContractPriceRankingTableProps> = ({
  data,
  expandedRows,
  onRowToggle,
  isLoading = false,
  onPriceAgainstClick,
}) => {
  // Hooks must be called unconditionally at the top (before any early returns)
  const dataWithKeys = useMemo(() => {
    return data.map(item => ({
      ...item,
      uniqueKey: `${item.ndc}-${item.price_type}-${item.price_rank}`,
    }));
  }, [data]);

  const handleRowToggle = useCallback(
    (e: any) => {
      // PrimeReact's onRowToggle receives event object with data property
      // The actual expanded rows object is in e.data
      // Structure: { [dataKey]: rowData } for each expanded row
      // Example: { "57896-0102-01-FSS-1": <rowData> }
      let expandedRowsData = e;

      // Extract data from event object if it exists
      if (e && typeof e === 'object' && 'data' in e) {
        expandedRowsData = e.data;

        // Handle nested data structures (in case of multiple nesting)
        while (
          expandedRowsData &&
          typeof expandedRowsData === 'object' &&
          'data' in expandedRowsData &&
          Object.keys(expandedRowsData).length === 1
        ) {
          expandedRowsData = expandedRowsData.data;
        }
      }

      if (onRowToggle) {
        onRowToggle(expandedRowsData);
      } else {
        console.error('🔴 onRowToggle is not defined!');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onRowToggle, expandedRows]
  );

  if (isLoading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#522d6e' }}>
        <i
          className="pi pi-spinner pi-spin"
          style={{ fontSize: '2rem', marginBottom: '0.5rem' }}
        ></i>
        <p>Loading contract price ranking data...</p>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
        <i className="pi pi-chart-bar" style={{ fontSize: '2rem', marginBottom: '0.5rem' }}></i>
        <p>No contract price ranking data available.</p>
      </div>
    );
  }

  return (
    <DataTable
      value={dataWithKeys}
      className={classes.purpleTable}
      expandedRows={expandedRows || {}}
      onRowToggle={handleRowToggle}
      rowExpansionTemplate={(rowData: ContractPriceRankingItem) => {
        const extraData = rowData.extra_data || {};

        return (
          <div className={classes.expandedRow}>
            <div className={classes.expandedRowHeader}>
              <h4>Additional Contract Details</h4>
            </div>
            <div className={classes.expandedRowGrid}>
              <div className={classes.expandedField}>
                <label>
                  <i className="pi pi-calendar" style={{ marginRight: '0.5rem' }}></i>
                  Contract Start Date
                </label>
                <span className={classes.expandedValue}>
                  {extraData.contract_start_date || (
                    <span className={classes.noData}>Not available</span>
                  )}
                </span>
              </div>
              <div className={classes.expandedField}>
                <label>
                  <i className="pi pi-chart-line" style={{ marginRight: '0.5rem' }}></i>% of
                  Commercial Market (IQVIA)
                </label>
                <span className={classes.expandedValue}>
                  {extraData.percentage_of_commercial_market || (
                    <span className={classes.noData}>Not available</span>
                  )}
                </span>
              </div>
              <div className={classes.expandedField}>
                <label>
                  <i className="pi pi-building" style={{ marginRight: '0.5rem' }}></i>% of
                  Government Market
                </label>
                <span className={classes.expandedValue}>
                  {extraData.percentage_of_government_market || (
                    <span className={classes.noData}>Not available</span>
                  )}
                </span>
              </div>
              <div className={classes.expandedField}>
                <label>
                  <i className="pi pi-info-circle" style={{ marginRight: '0.5rem' }}></i>
                  NAD
                </label>
                <span className={classes.expandedValue}>
                  {extraData.nad || <span className={classes.noData}>Not available</span>}
                </span>
              </div>
              <div className={classes.expandedField}>
                <label>
                  <i className="pi pi-clock" style={{ marginRight: '0.5rem' }}></i>
                  NAD Expiration Date
                </label>
                <span className={classes.expandedValue}>
                  {extraData.nad_expiry || <span className={classes.noData}>Not available</span>}
                </span>
              </div>
            </div>
          </div>
        );
      }}
      dataKey="uniqueKey"
    >
      <Column expander style={{ width: '3rem' }} />
      <Column
        field="price_rank"
        header="Price Rank"
        style={{ width: '100px' }}
        body={(rowData: ContractPriceRankingItem) => rowData.price_rank}
      />
      <Column
        field="contracter_name"
        header="Contractor Name"
        body={(rowData: ContractPriceRankingItem) => {
          // Map contracter_name from API to "Contractor Name" column
          let value = rowData?.contracter_name;

          // Handle string "null" or "undefined" edge cases
          if (
            typeof value === 'string' &&
            (value.toLowerCase() === 'null' || value.toLowerCase() === 'undefined')
          ) {
            value = null;
          }

          // Trim whitespace if it's a string
          if (typeof value === 'string') {
            value = value.trim();
          }

          // Show "-" for null, undefined, or empty string
          if (value === null || value === undefined || value === '') {
            return <span className={classes.noData}>-</span>;
          }

          // Return the actual string value
          return String(value);
        }}
      />
      <Column
        field="labeller_name"
        header="Labeler Name"
        body={(rowData: ContractPriceRankingItem) => rowData.labeller_name}
      />
      <Column
        field="ndc"
        header="NDC 11"
        body={(rowData: ContractPriceRankingItem) => rowData.ndc}
      />
      <Column
        field="price_type"
        header="Price Type"
        body={(rowData: ContractPriceRankingItem) => rowData.price_type}
      />
      <Column
        field="price"
        header="Price SU"
        body={(rowData: ContractPriceRankingItem) => `$${formatDecimal(rowData.price)}`}
      />
      <Column
        field="price_eu"
        header="Price EU"
        body={(rowData: ContractPriceRankingItem) => {
          if (
            rowData.price_eu === null ||
            rowData.price_eu === undefined ||
            (typeof rowData.price_eu === 'number' && Number.isNaN(rowData.price_eu))
          ) {
            return '-';
          }
          return `$${formatDecimal(rowData.price_eu)}`;
        }}
      />
      <Column
        field="current_wac"
        header="Current WAC"
        body={(rowData: ContractPriceRankingItem) => {
          // Handle null/undefined current_wac - show "-" instead of 0
          if (
            rowData.current_wac === null ||
            rowData.current_wac === undefined ||
            rowData.current_wac === 0
          ) {
            return '-';
          }
          return `$${formatDecimal(rowData.current_wac)}`;
        }}
      />
      <Column
        field="current_market_share"
        header="Current Market Share"
        body={(rowData: ContractPriceRankingItem) => {
          // Map current_market_share from API to "Current Market Share" column
          let value = rowData?.current_market_share;

          // Handle string "null" or "undefined" edge cases
          if (
            typeof value === 'string' &&
            (value.toLowerCase() === 'null' || value.toLowerCase() === 'undefined')
          ) {
            value = null;
          }

          // Trim whitespace if it's a string
          if (typeof value === 'string') {
            value = value.trim();
          }

          // Show "-" for null, undefined, or empty string
          if (value === null || value === undefined || value === '') {
            return <span className={classes.noData}>-</span>;
          }

          // Show the actual value (including "0.00%" if that's what the API returns)
          // API returns string (e.g. "0.78%"); support number for robustness
          const num = typeof value === 'string' ? parseFloat(value) : value;
          if (typeof num === 'number' && !Number.isNaN(num)) {
            return `${num.toFixed(2)}%`;
          }
          return String(value);
        }}
      />
      {onPriceAgainstClick && (
        <Column
          header="Action"
          style={{ width: '12rem', textAlign: 'center' }}
          body={(rowData: ContractPriceRankingItem) => (
            <Button
              label="Price Against"
              icon="pi pi-dollar"
              onClick={() => onPriceAgainstClick(rowData)}
              className={classes.priceAgainstButton}
              outlined
            />
          )}
        />
      )}
    </DataTable>
  );
};
